import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { loadInitialData } from '../api/client';
import { useAuth, useGoogleButton } from '../contexts/AuthContext';
import type { DirectoryEntry, Facets, Branding } from '../types/api';
import type { UserRole } from '../types/roles';
import { isCategoryAllowedForRoles, extractAllUserProfilesFromEntries, resolveUserRoleProfile } from '../utils/roleUtils';
import { ensureActiveSheetTabs, getFormattedEntryTitle, groupEntriesBySpreadsheetForSearch } from '../utils/tabUtils';
import { evaluateSearchMatch, matchesSearch } from '../utils/searchUtils';
import { getLocalAdminSettings } from '../utils/adminSync';
import { APP_VERSION } from '../config/version';
import printableDocsData from '../data/printableDocuments.json';

interface PrintableDoc {
  id: string;
  docNumber: string;
  category: string;
  title: string;
  fullName: string;
  pdfUrl: string;
  docUrl: string;
  updateDate: string;
}

interface TeamOption {
  name: string;
  count: number;
  isAssigned?: boolean;
}

interface MultiSelectTeamDropdownProps {
  selectedTeams: string[];
  onChange: (teams: string[]) => void;
  teams: TeamOption[];
  userAssignedTeams?: string[];
  totalSheetsCount: number;
  colorScheme?: 'purple' | 'blue' | 'emerald' | 'green';
}

function MultiSelectTeamDropdown({
  selectedTeams,
  onChange,
  teams,
  userAssignedTeams = [],
  totalSheetsCount,
  colorScheme = 'purple',
}: MultiSelectTeamDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [teamSearch, setTeamSearch] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const isAllSelected = selectedTeams.length === 0 || selectedTeams.includes('All');

  const handleToggleTeam = (teamName: string) => {
    if (teamName === 'All') {
      onChange(['All']);
      return;
    }

    let current = selectedTeams.filter((t) => t !== 'All');
    if (current.includes(teamName)) {
      current = current.filter((t) => t !== teamName);
    } else {
      current = [...current, teamName];
    }

    if (current.length === 0) {
      onChange(['All']);
    } else {
      onChange(current);
    }
  };

  const handleSelectAll = () => {
    onChange(['All']);
  };

  const handleSelectMyTeams = () => {
    if (userAssignedTeams.length > 0) {
      onChange(userAssignedTeams);
    }
  };

  const handleClearAll = () => {
    onChange(['All']);
  };

  // Filtered teams list based on internal search
  const filteredTeams = useMemo(() => {
    if (!teamSearch.trim()) return teams;
    return teams.filter((t) => matchesSearch(teamSearch, { primary: t.name }));
  }, [teams, teamSearch]);

  const assignedTeamsList = useMemo(() => filteredTeams.filter((t) => t.isAssigned), [filteredTeams]);
  const otherTeamsList = useMemo(() => filteredTeams.filter((t) => !t.isAssigned), [filteredTeams]);

  // Button label text
  const buttonLabel = useMemo(() => {
    if (isAllSelected) {
      return `🌐 All Teams (${totalSheetsCount})`;
    }
    if (selectedTeams.length === 1) {
      const match = teams.find((t) => t.name === selectedTeams[0]);
      return `👥 ${selectedTeams[0]} (${match?.count ?? 0})`;
    }
    if (selectedTeams.length === 2) {
      return `👥 ${selectedTeams[0]}, ${selectedTeams[1]}`;
    }
    return `👥 ${selectedTeams.length} Teams Selected`;
  }, [isAllSelected, selectedTeams, teams, totalSheetsCount]);

  const isEmerald = colorScheme === 'emerald' || colorScheme === 'green';
  const isPurple = colorScheme === 'purple';

  const borderFocusClass = isEmerald
    ? 'border-emerald-300 dark:border-emerald-700/60 focus:ring-emerald-500'
    : isPurple
    ? 'border-purple-300 dark:border-purple-700/60 focus:ring-purple-500'
    : 'border-blue-300 dark:border-blue-700/60 focus:ring-blue-500';

  const accentBadgeClass = isEmerald
    ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300'
    : isPurple
    ? 'bg-purple-100 text-purple-800 dark:bg-purple-950/60 dark:text-purple-300'
    : 'bg-blue-100 text-blue-800 dark:bg-blue-950/60 dark:text-blue-300';

  const accentSelectedRowClass = isEmerald
    ? 'bg-emerald-50/80 dark:bg-emerald-950/40 text-emerald-900 dark:text-emerald-200'
    : isPurple
    ? 'bg-purple-50/80 dark:bg-purple-950/40 text-purple-900 dark:text-purple-200'
    : 'bg-blue-50/80 dark:bg-blue-950/40 text-blue-900 dark:text-blue-200';

  const accentFocusRing = isEmerald
    ? 'focus:ring-emerald-500'
    : isPurple
    ? 'focus:ring-purple-500'
    : 'focus:ring-blue-500';

  const accentActiveTextClass = isEmerald
    ? 'text-emerald-600 dark:text-emerald-400 font-bold'
    : isPurple
    ? 'text-purple-600 dark:text-purple-400 font-bold'
    : 'text-blue-600 dark:text-blue-400 font-bold';

  return (
    <div className="relative w-full" ref={dropdownRef}>
      {/* Trigger Button */}
      <div
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full text-xs bg-white dark:bg-gray-800 border ${borderFocusClass} rounded-lg px-2.5 py-1.5 text-gray-900 dark:text-gray-100 font-semibold shadow-sm cursor-pointer flex items-center justify-between gap-1.5 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors select-none`}
      >
        <span className="truncate">{buttonLabel}</span>
        <div className="flex items-center gap-1 flex-shrink-0">
          {!isAllSelected && (
            <span
              onClick={(e) => {
                e.stopPropagation();
                handleClearAll();
              }}
              title="Reset to All Teams"
              className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 p-0.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors text-xs leading-none"
            >
              ✕
            </span>
          )}
          <svg
            className={`w-3.5 h-3.5 text-gray-500 transition-transform duration-150 ${isOpen ? 'transform rotate-180' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      {/* Dropdown Popover */}
      {isOpen && (
        <div className="absolute left-0 top-full mt-1 w-full min-w-[260px] sm:w-72 bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 z-50 overflow-hidden flex flex-col max-h-80 animate-in fade-in zoom-in-95 duration-100">
          {/* Internal Search */}
          <div className="p-2 border-b border-gray-100 dark:border-gray-700/80 bg-gray-50 dark:bg-gray-900/60">
            <input
              type="text"
              placeholder="Filter teams..."
              value={teamSearch}
              onChange={(e) => setTeamSearch(e.target.value)}
              className={`w-full text-xs bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg px-2.5 py-1 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-1 ${accentFocusRing}`}
              autoFocus
            />
            {/* Quick Action Buttons */}
            <div className="flex items-center justify-between gap-1 mt-1.5 pt-1 text-[10px]">
              <button
                type="button"
                onClick={handleSelectAll}
                className={`font-semibold px-1.5 py-0.5 rounded hover:underline ${
                  isAllSelected ? accentActiveTextClass : 'text-gray-600 dark:text-gray-400'
                }`}
              >
                Select All
              </button>
              {userAssignedTeams.length > 0 && (
                <button
                  type="button"
                  onClick={handleSelectMyTeams}
                  className="font-semibold px-1.5 py-0.5 rounded text-amber-600 dark:text-amber-400 hover:underline"
                >
                  My Teams
                </button>
              )}
              <button
                type="button"
                onClick={handleClearAll}
                className="font-medium px-1.5 py-0.5 rounded text-gray-500 hover:text-gray-800 dark:hover:text-gray-200 hover:underline"
              >
                Reset
              </button>
            </div>
          </div>

          {/* Teams List */}
          <div className="flex-1 overflow-y-auto p-1.5 space-y-0.5 divide-y divide-gray-100 dark:divide-gray-800/40">
            {/* Option: All Teams */}
            <div
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleToggleTeam('All');
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleToggleTeam('All');
                }
              }}
              className={`flex items-center justify-between px-2.5 py-1.5 rounded-lg text-xs cursor-pointer select-none transition-colors ${
                isAllSelected ? accentSelectedRowClass : 'hover:bg-gray-100 dark:hover:bg-gray-700/60 text-gray-800 dark:text-gray-200'
              }`}
            >
              <div className="flex items-center gap-2 min-w-0">
                <div
                  className={`w-3.5 h-3.5 rounded border flex items-center justify-center transition-colors flex-shrink-0 ${
                    isAllSelected
                      ? isEmerald
                        ? 'bg-emerald-600 border-emerald-600 text-white'
                        : isPurple
                        ? 'bg-purple-600 border-purple-600 text-white'
                        : 'bg-blue-600 border-blue-600 text-white'
                      : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700'
                  }`}
                >
                  {isAllSelected && (
                    <svg className="w-2.5 h-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </div>
                <span className="font-semibold truncate">🌐 All Teams</span>
              </div>
              <span className={`text-[10px] font-bold px-1.5 py-0.2 rounded-full ${accentBadgeClass}`}>
                {totalSheetsCount}
              </span>
            </div>

            {/* Group: Assigned Teams */}
            {assignedTeamsList.length > 0 && (
              <div className="pt-1.5">
                <div className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-amber-600 dark:text-amber-400">
                  Your Assigned Teams
                </div>
                {assignedTeamsList.map((team) => {
                  const isChecked = !isAllSelected && selectedTeams.includes(team.name);
                  return (
                    <div
                      key={`assigned-${team.name}`}
                      role="button"
                      tabIndex={0}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleToggleTeam(team.name);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleToggleTeam(team.name);
                        }
                      }}
                      className={`flex items-center justify-between px-2.5 py-1.5 rounded-lg text-xs cursor-pointer select-none transition-colors ${
                        isChecked ? accentSelectedRowClass : 'hover:bg-gray-100 dark:hover:bg-gray-700/60 text-gray-800 dark:text-gray-200'
                      }`}
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <div
                          className={`w-3.5 h-3.5 rounded border flex items-center justify-center transition-colors flex-shrink-0 ${
                            isChecked
                              ? isEmerald
                                ? 'bg-emerald-600 border-emerald-600 text-white'
                                : isPurple
                                ? 'bg-purple-600 border-purple-600 text-white'
                                : 'bg-blue-600 border-blue-600 text-white'
                              : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700'
                          }`}
                        >
                          {isChecked && (
                            <svg className="w-2.5 h-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                        <span className="truncate font-medium">{team.name}</span>
                      </div>
                      <span className="text-[10px] text-gray-500 dark:text-gray-400 font-semibold px-1.5 py-0.2">
                        {team.count}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Group: Other Teams */}
            {otherTeamsList.length > 0 && (
              <div className="pt-1.5">
                {assignedTeamsList.length > 0 && (
                  <div className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-gray-400 dark:text-gray-500">
                    👥 Other Teams
                  </div>
                )}
                {otherTeamsList.map((team) => {
                  const isChecked = !isAllSelected && selectedTeams.includes(team.name);
                  return (
                    <div
                      key={`other-${team.name}`}
                      role="button"
                      tabIndex={0}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleToggleTeam(team.name);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleToggleTeam(team.name);
                        }
                      }}
                      className={`flex items-center justify-between px-2.5 py-1.5 rounded-lg text-xs cursor-pointer select-none transition-colors ${
                        isChecked ? accentSelectedRowClass : 'hover:bg-gray-100 dark:hover:bg-gray-700/60 text-gray-800 dark:text-gray-200'
                      }`}
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <div
                          className={`w-3.5 h-3.5 rounded border flex items-center justify-center transition-colors flex-shrink-0 ${
                            isChecked
                              ? isEmerald
                                ? 'bg-emerald-600 border-emerald-600 text-white'
                                : isPurple
                                ? 'bg-purple-600 border-purple-600 text-white'
                                : 'bg-blue-600 border-blue-600 text-white'
                              : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700'
                          }`}
                        >
                          {isChecked && (
                            <svg className="w-2.5 h-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                        <span className="truncate font-medium">👥 {team.name}</span>
                      </div>
                      <span className="text-[10px] text-gray-500 dark:text-gray-400 font-semibold px-1.5 py-0.2">
                        {team.count}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}

            {filteredTeams.length === 0 && (
              <div className="p-3 text-center text-xs text-gray-400">
                No teams match "{teamSearch}"
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function WorkflowTestPage() {
  const auth = useAuth();
  const { renderButton } = useGoogleButton();
  const googleButtonRef = useRef<HTMLDivElement>(null);

  const [entries, setEntries] = useState<DirectoryEntry[]>([]);
  const [_facets, setFacets] = useState<Facets | null>(null);
  const [_branding, setBranding] = useState<Branding | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Column 1 state (Daily Tasks)
  const [col1SelectedTeams, setCol1SelectedTeams] = useState<string[]>(['All']);
  const [col1Search, setCol1Search] = useState<string>('');

  // Column 2 state (Printable Documents Hub)
  const [col2Search, setCol2Search] = useState<string>('');
  const [previewDoc, setPreviewDoc] = useState<PrintableDoc | null>(null);

  // Column 3 state (SSD Team Sheets Directory)
  const [col3Search, setCol3Search] = useState<string>('');
  const [col3SelectedTeams, setCol3SelectedTeams] = useState<string[]>(['All']);

  // Pinned sheets state (synced with global localStorage)
  const activeUserEmail = useMemo(() => {
    return (auth.previewUserEmail || auth.user?.email || '').toLowerCase().trim();
  }, [auth.previewUserEmail, auth.user?.email]);

  const [pinnedIds, setPinnedIds] = useState<string[]>(() => {
    try {
      const perUserKey = activeUserEmail ? `ssd-pinned-${activeUserEmail}` : 'ssd-intranet-pinned-sheets';
      const stored = localStorage.getItem(perUserKey) || localStorage.getItem('ssd-intranet-pinned-sheets');
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  // Keep pinned state synced per user
  useEffect(() => {
    if (!activeUserEmail) return;
    try {
      const perUserKey = `ssd-pinned-${activeUserEmail}`;
      const stored = localStorage.getItem(perUserKey) || localStorage.getItem('ssd-intranet-pinned-sheets');
      if (stored) {
        setPinnedIds(JSON.parse(stored));
      }
    } catch {
      // ignore
    }
  }, [activeUserEmail]);

  const togglePin = (id: string, e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setPinnedIds((prev) => {
      const next = prev.includes(id) ? prev.filter((p) => p !== id) : [id, ...prev];
      try {
        if (activeUserEmail) {
          localStorage.setItem(`ssd-pinned-${activeUserEmail}`, JSON.stringify(next));
        }
        localStorage.setItem('ssd-intranet-pinned-sheets', JSON.stringify(next));
      } catch {
        // ignore
      }
      return next;
    });
  };

  // Google Login button rendering
  useEffect(() => {
    if (!auth.isAuthenticated && !auth.isLoading && googleButtonRef.current) {
      googleButtonRef.current.innerHTML = '';
      renderButton(googleButtonRef.current);
    }
  }, [auth.isAuthenticated, auth.isLoading, renderButton]);

  // Load live initial data
  useEffect(() => {
    async function init() {
      try {
        setLoading(true);
        const data = await loadInitialData();
        if (data.directory.entries && data.directory.entries.length > 0) {
          const verifiedEntries = ensureActiveSheetTabs(data.directory.entries);
          setEntries(verifiedEntries);
        }
        if (data.facets.success && data.facets.facets) {
          setFacets(data.facets.facets);
        }
        if (data.branding.success && data.branding.branding) {
          setBranding(data.branding.branding);
        }
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load intranet data');
      } finally {
        setLoading(false);
      }
    }
    init();
  }, []);

  const userRoles: UserRole[] = useMemo(() => (auth.user?.roles || ['Staff']) as UserRole[], [auth.user?.roles]);

  const allSheetUserProfiles = useMemo(() => {
    return extractAllUserProfilesFromEntries(entries);
  }, [entries]);

  const activeSheetUserProfile = useMemo(() => {
    if (!activeUserEmail) return null;
    return allSheetUserProfiles.find((p) => p.email === activeUserEmail) || null;
  }, [activeUserEmail, allSheetUserProfiles]);

  const userRoleProfile = useMemo(() => {
    if (!activeUserEmail) return null;
    return resolveUserRoleProfile(activeUserEmail);
  }, [activeUserEmail]);

  const userAssignedTeams = useMemo<string[]>(() => {
    if (!activeUserEmail) return [];
    const authAssigned = (auth.user as any)?.assignedTeams as string[] | undefined;
    if (authAssigned && Array.isArray(authAssigned) && authAssigned.length > 0) {
      return authAssigned.filter((t: string) => t && t.toLowerCase() !== 'no team');
    }
    if (activeSheetUserProfile?.assignedTeams && activeSheetUserProfile.assignedTeams.length > 0) {
      return activeSheetUserProfile.assignedTeams.filter((t: string) => t && t.toLowerCase() !== 'no team');
    }
    if (userRoleProfile?.assignedTeams && userRoleProfile.assignedTeams.length > 0) {
      return userRoleProfile.assignedTeams.filter((t: string) => t && t.toLowerCase() !== 'no team');
    }
    const primary = auth.user?.primaryTeam || activeSheetUserProfile?.primaryTeam || userRoleProfile?.primaryTeam || '';
    if (primary && primary.toLowerCase() !== 'no team') return [primary];
    return [];
  }, [activeUserEmail, auth.user, activeSheetUserProfile, userRoleProfile]);

  // Default teams in Column 1 and Column 3 to user's assigned team(s)
  const lastInitializedEmailRef = useRef<string | null>(null);
  useEffect(() => {
    if (!activeUserEmail) return;
    if (lastInitializedEmailRef.current !== activeUserEmail) {
      if (userAssignedTeams.length > 0) {
        lastInitializedEmailRef.current = activeUserEmail;
        setCol1SelectedTeams(userAssignedTeams);
        setCol3SelectedTeams(userAssignedTeams);
      } else if (!loading) {
        lastInitializedEmailRef.current = activeUserEmail;
        setCol1SelectedTeams(['All']);
        setCol3SelectedTeams(['All']);
      }
    }
  }, [activeUserEmail, userAssignedTeams, loading]);

  // Team sheet counts based on entries and user role permissions
  const teamSheetCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    entries.forEach((entry) => {
      if (!entry) return;
      const categories = Array.isArray(entry.categories) ? entry.categories : [];
      if (categories.length > 0 && !categories.some((c) => isCategoryAllowedForRoles(c, userRoles))) {
        return;
      }
      if (entry.team) {
        entry.team
          .split(',')
          .map((t) => t.trim())
          .filter(Boolean)
          .forEach((t) => {
            const key = t.toLowerCase();
            counts[key] = (counts[key] || 0) + 1;
          });
      }
    });
    return counts;
  }, [entries, userRoles]);

  // Sorted list of all valid teams with assigned teams first
  const sortedTeamsList = useMemo(() => {
    const adminSettings = getLocalAdminSettings();
    const deletedTeams = adminSettings.deletedTeams || [];
    const deletedSet = new Set(deletedTeams.map((t) => t.toLowerCase().trim()));

    const userAssignedLowerSet = new Set(
      userAssignedTeams.map((t: string) => t.toLowerCase().trim())
    );

    const allTeamNamesSet = new Set<string>();
    (adminSettings.customTeams || []).forEach((t) => {
      if (t) allTeamNamesSet.add(t.trim());
    });
    (adminSettings.sheetCustomTeams || []).forEach((t) => {
      if (t) allTeamNamesSet.add(t.trim());
    });
    entries.forEach((e) => {
      if (e && typeof e.team === 'string') {
        e.team
          .split(',')
          .map((t) => t.trim())
          .filter(Boolean)
          .forEach((t) => allTeamNamesSet.add(t));
      }
    });
    userAssignedTeams.forEach((t: string) => {
      if (t) allTeamNamesSet.add(t.trim());
    });
    ['Front Desk', 'Billing Team', 'Clinical', 'Operations', 'Admin Team', 'SuperAdmin', "Dr. Mario's Team", 'Lab Team'].forEach((t) =>
      allTeamNamesSet.add(t)
    );

    const validTeamNames = Array.from(allTeamNamesSet).filter((t) => {
      const lower = t.toLowerCase().trim();
      return (
        lower !== '' &&
        lower !== 'management' &&
        lower !== 'no team' &&
        lower !== 'unassigned' &&
        !deletedSet.has(lower)
      );
    });

    return validTeamNames
      .map((name) => {
        const count = teamSheetCounts[name.toLowerCase().trim()] || 0;
        const isAssigned = userAssignedLowerSet.has(name.toLowerCase().trim());
        return { name, count, isAssigned };
      })
      .sort((a, b) => {
        if (a.isAssigned && !b.isAssigned) return -1;
        if (!a.isAssigned && b.isAssigned) return 1;
        return a.name.localeCompare(b.name);
      });
  }, [entries, userAssignedTeams, teamSheetCounts]);

  // =========================================================================
  // COLUMN 1: Daily Operational Workflow Sheets
  // =========================================================================
  const col1Entries = useMemo(() => {
    const pinnedSet = new Set(pinnedIds);

    const scoredList: Array<{ entry: DirectoryEntry; score: number }> = [];

    entries.forEach((entry) => {
      if (!entry) return;
      const isPinned = pinnedSet.has(entry.id);

      // Multi-team filter (Pinned sheets always stay at the top across all team views!)
      if (col1SelectedTeams.length > 0 && !col1SelectedTeams.includes('All')) {
        const teamStr = typeof entry.team === 'string' ? entry.team.toUpperCase() : '';
        const entryTeams = teamStr.split(',').map((t) => t.trim()).filter(Boolean);
        const matchesTeam = col1SelectedTeams.some((sel) => {
          const selUpper = sel.toUpperCase().trim();
          if (selUpper === 'SUPERADMIN') return teamStr.includes('ADMIN');
          return entryTeams.some((et) => et.includes(selUpper) || selUpper.includes(et));
        });
        if (!matchesTeam && !isPinned) return;
      }

      // Role check
      const categories = Array.isArray(entry.categories) ? entry.categories : [];
      if (categories.length > 0) {
        if (!categories.some((c) => isCategoryAllowedForRoles(c, userRoles))) return;
      }

      // Column 1 Search with tokenized matching, punctuation normalization & fuzzy tolerance
      let searchScore = 0;
      if (col1Search.trim()) {
        const match = evaluateSearchMatch(col1Search, {
          primary: getFormattedEntryTitle(entry),
          secondary: [
            entry.name,
            entry.description,
            ...((entry as any).activeTabs || []).map((t: any) => t.name)
          ],
          tertiary: [
            entry.team,
            ...categories
          ]
        });
        if (!match.matched) return;
        searchScore = match.score;
      }

      scoredList.push({ entry, score: searchScore });
    });

    // Sort: Pinned first, then search relevance score (if searching), then sequential workflow priority
    scoredList.sort((itemA, itemB) => {
      const a = itemA.entry;
      const b = itemB.entry;
      const aPinned = pinnedSet.has(a.id);
      const bPinned = pinnedSet.has(b.id);
      if (aPinned && !bPinned) return -1;
      if (!aPinned && bPinned) return 1;
      if (aPinned && bPinned) {
        return pinnedIds.indexOf(a.id) - pinnedIds.indexOf(b.id);
      }

      // If search query is active, rank higher search relevance scores first
      if (col1Search.trim() && itemA.score !== itemB.score) {
        return itemB.score - itemA.score;
      }

      // Morning workflow sequencing heuristics
      const getWorkflowWeight = (e: DirectoryEntry) => {
        const title = (e.name || '').toLowerCase();
        if (title.includes('huddle') || title.includes('morning') || title.includes('checklist') || title.includes('daily recap')) return 1;
        if (title.includes('schedule') || title.includes('intake') || title.includes('front desk') || title.includes('reception')) return 2;
        if (title.includes('surgery') || title.includes('clinical') || title.includes('mohs') || title.includes('procedure')) return 3;
        if (title.includes('task') || title.includes('microtask') || title.includes('todo') || title.includes('log')) return 4;
        if (title.includes('operation') || title.includes('lab') || title.includes('billing')) return 5;
        return 6;
      };

      const weightDiff = getWorkflowWeight(a) - getWorkflowWeight(b);
      if (weightDiff !== 0) return weightDiff;
      return a.name.localeCompare(b.name);
    });

    return scoredList.map((item) => item.entry);
  }, [entries, col1SelectedTeams, col1Search, pinnedIds, userRoles]);

  // =========================================================================
  // COLUMN 2: Printable Documents Hub
  // =========================================================================
  const allPrintableDocs: PrintableDoc[] = useMemo(() => {
    const raw = (printableDocsData as any[]).map((doc, idx) => ({
      id: doc.id || `pdoc-${idx + 1}`,
      docNumber: doc.docNumber || `${idx + 1}`,
      category: doc.category || 'General Forms',
      title: (doc.title || doc.fullName || 'Clinical Form').trim(),
      fullName: (doc.fullName || doc.title || 'Clinical Form').trim(),
      pdfUrl: doc.pdfUrl || '',
      docUrl: doc.docUrl || '',
      updateDate: doc.updateDate || '2025-02-18',
    }));

    const seenTitles = new Set<string>();
    const seenIds = new Set<string>();
    const deduped: PrintableDoc[] = [];

    for (const doc of raw) {
      if (!doc.title || doc.title.toLowerCase() === 'document #' || (!doc.pdfUrl && !doc.docUrl)) {
        continue;
      }
      const normTitle = doc.title.toLowerCase();
      if (seenTitles.has(normTitle) || seenIds.has(doc.id)) {
        continue;
      }
      seenTitles.add(normTitle);
      seenIds.add(doc.id);
      deduped.push(doc);
    }

    return deduped;
  }, []);

  const col2Docs = useMemo(() => {
    const pinnedSet = new Set(pinnedIds);
    const scoredList: Array<{ doc: PrintableDoc; score: number }> = [];

    allPrintableDocs.forEach((doc) => {
      let searchScore = 0;
      if (col2Search.trim()) {
        const match = evaluateSearchMatch(col2Search, {
          primary: doc.title,
          secondary: [doc.fullName, doc.docNumber, doc.id],
          tertiary: doc.category
        });
        if (!match.matched) return;
        searchScore = match.score;
      }
      scoredList.push({ doc, score: searchScore });
    });

    scoredList.sort((itemA, itemB) => {
      const aPinned = pinnedSet.has(itemA.doc.id);
      const bPinned = pinnedSet.has(itemB.doc.id);
      if (aPinned && !bPinned) return -1;
      if (!aPinned && bPinned) return 1;

      if (col2Search.trim() && itemA.score !== itemB.score) {
        return itemB.score - itemA.score;
      }

      return 0;
    });

    return scoredList.map((item) => item.doc);
  }, [allPrintableDocs, col2Search, pinnedIds]);

  // Helper to convert Google Drive / Docs links to embeddable preview URLs
  const getEmbedUrl = (url: string) => {
    if (!url) return '';
    if (url.includes('drive.google.com/file/d/')) {
      const match = url.match(/\/file\/d\/([^/]+)/);
      if (match && match[1]) {
        return `https://drive.google.com/file/d/${match[1]}/preview`;
      }
    }
    if (url.includes('docs.google.com/document/d/')) {
      const match = url.match(/\/document\/d\/([^/]+)/);
      if (match && match[1]) {
        return `https://docs.google.com/document/d/${match[1]}/preview`;
      }
    }
    return url;
  };

  // =========================================================================
  // COLUMN 3: SSD Team Sheets Directory (Live Site Sheets by Team)
  // =========================================================================
  const col3Entries = useMemo(() => {
    const pinnedSet = new Set(pinnedIds);

    const accessibleEntries = entries.filter((entry) => {
      if (!entry) return false;
      const isPinned = pinnedSet.has(entry.id);

      // Role check
      const categories = Array.isArray(entry.categories) ? entry.categories : [];
      if (categories.length > 0) {
        if (!categories.some((c) => isCategoryAllowedForRoles(c, userRoles))) return false;
      }

      // Multi-team filter (Pinned sheets always stay at top across all team views!)
      if (col3SelectedTeams.length > 0 && !col3SelectedTeams.includes('All')) {
        const teamStr = typeof entry.team === 'string' ? entry.team.toUpperCase() : '';
        const entryTeams = teamStr.split(',').map((t) => t.trim()).filter(Boolean);
        const matchesTeam = col3SelectedTeams.some((sel) => {
          const selUpper = sel.toUpperCase().trim();
          if (selUpper === 'SUPERADMIN') return teamStr.includes('ADMIN');
          return entryTeams.some((et) => et.includes(selUpper) || selUpper.includes(et));
        });
        if (!matchesTeam && !isPinned) return false;
      }

      return true;
    });

    let scoredList: Array<{ entry: DirectoryEntry; score: number }>;

    // If search is active, collapse results to the main tab per spreadsheet with sub-tabs attached
    if (col3Search.trim()) {
      scoredList = groupEntriesBySpreadsheetForSearch(accessibleEntries, col3Search, pinnedSet);
    } else {
      scoredList = accessibleEntries.map((entry) => ({ entry, score: 0 }));
    }

    // Sort: Pinned first, then search relevance score (if searching), then alphabetically by title
    scoredList.sort((itemA, itemB) => {
      const a = itemA.entry;
      const b = itemB.entry;
      const aPinned = pinnedSet.has(a.id);
      const bPinned = pinnedSet.has(b.id);
      if (aPinned && !bPinned) return -1;
      if (!aPinned && bPinned) return 1;
      if (aPinned && bPinned) {
        return pinnedIds.indexOf(a.id) - pinnedIds.indexOf(b.id);
      }

      // If search query is active, rank higher search relevance scores first
      if (col3Search.trim() && itemA.score !== itemB.score) {
        return itemB.score - itemA.score;
      }

      return (a.name || '').localeCompare(b.name || '');
    });

    return scoredList.map((item) => item.entry);
  }, [entries, col3SelectedTeams, col3Search, pinnedIds, userRoles, userAssignedTeams]);

  if (!auth.isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md text-center">
          <img src="/ssd-logo.png" alt="SSD Logo" className="mx-auto h-16 w-auto object-contain mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">SSD 3-Column Workflow Experience</h2>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">Please sign in with your SSD Google account to test this layout</p>
          <div className="mt-6 flex justify-center">
            <div ref={googleButtonRef} />
          </div>
          <div className="mt-4">
            <Link to="/" className="text-sm font-medium text-amber-600 hover:text-amber-500">
              ← Return to Standard Intranet View
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-950 flex flex-col text-gray-900 dark:text-gray-100">
      {/* Top Test Notification Bar */}
      <div className="bg-gradient-to-r from-amber-600 via-indigo-600 to-purple-700 text-white px-4 py-2 text-xs md:text-sm font-medium flex items-center justify-between shadow-md z-40">
        <div className="flex items-center gap-2">
          <span className="bg-white/20 backdrop-blur-sm text-white text-[11px] px-2.5 py-0.5 rounded-full font-bold uppercase tracking-wider">
            🧪 3-Column Test Preview
          </span>
          <span className="hidden sm:inline">
            Interactive Test Version: <strong>Daily Tasks</strong> • <strong>Printable Documents</strong> • <strong>All Sheets</strong>
          </span>
        </div>
        <div className="flex items-center gap-3">
          <Link
            to="/"
            className="bg-white/20 hover:bg-white/30 text-white text-xs px-3 py-1 rounded-lg font-semibold transition-colors flex items-center gap-1 shadow-sm"
          >
            ← Back to Live Intranet
          </Link>
        </div>
      </div>

      {/* Main Header */}
      <header className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 shadow-sm px-4 lg:px-8 py-4 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Link to="/" className="flex items-center gap-3 group">
            <img src="/ssd-logo.png" alt="SSD Logo" className="h-10 w-auto object-contain group-hover:scale-105 transition-transform" />
            <div>
              <h1 className="text-lg font-bold text-gray-900 dark:text-white leading-tight">
                Southern Skies Dermatology
              </h1>
              <p className="text-xs text-gray-500 dark:text-gray-400 font-medium">
                Unified Workflow Dashboard • {APP_VERSION}
              </p>
            </div>
          </Link>
        </div>

        {/* User Info & Quick Switch */}
        <div className="flex items-center gap-3">
          <div className="text-right hidden sm:block">
            <div className="text-xs font-bold text-gray-900 dark:text-white">{auth.user?.name || auth.user?.email}</div>
            <div className="text-[11px] text-amber-600 dark:text-amber-400 font-medium">
              Teams: {userAssignedTeams.join(', ') || 'General Staff'}
            </div>
          </div>
          {auth.user?.picture && (
            <img src={auth.user.picture} alt="" className="w-9 h-9 rounded-full ring-2 ring-amber-500/30" />
          )}
          <button
            onClick={() => auth.signOut()}
            className="text-xs bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 px-3 py-1.5 rounded-lg font-medium transition-colors"
          >
            Sign Out
          </button>
        </div>
      </header>

      {/* Error alert if any */}
      {error && (
        <div className="max-w-[1800px] w-full mx-auto p-4">
          <div className="bg-red-50 dark:bg-red-950/40 border-l-4 border-red-500 p-3 rounded text-xs text-red-700 dark:text-red-300">
            {error}
          </div>
        </div>
      )}

      {/* 3-Column Layout Container */}
      <main className="flex-1 p-4 lg:p-6 max-w-[1800px] w-full mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
          
          {/* ================================================================= */}
          {/* COLUMN 1: Daily Tasks */}
          {/* ================================================================= */}
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-md border border-gray-200 dark:border-gray-800 flex flex-col h-[calc(100vh-140px)] min-h-[700px] overflow-hidden">
            {/* Column Header */}
            <div className="p-4 bg-gradient-to-r from-emerald-50/80 to-teal-50/80 dark:from-emerald-950/30 dark:to-teal-950/30 border-b border-gray-200 dark:border-gray-800 flex-shrink-0">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-emerald-600 text-white flex items-center justify-center font-bold shadow-sm">
                    📋
                  </div>
                  <div>
                    <h2 className="text-base font-bold text-gray-900 dark:text-white leading-tight">
                      Daily Tasks
                    </h2>
                  </div>
                </div>
                <span className="bg-emerald-100 dark:bg-emerald-900/60 text-emerald-700 dark:text-emerald-300 text-xs font-bold px-2.5 py-0.5 rounded-full">
                  {col1Entries.length} Sheets
                </span>
              </div>

              {/* Space-Saving Multi-Select Team Dropdown & Search */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3">
                <div>
                  <MultiSelectTeamDropdown
                    selectedTeams={col1SelectedTeams}
                    onChange={setCol1SelectedTeams}
                    teams={sortedTeamsList}
                    userAssignedTeams={userAssignedTeams}
                    totalSheetsCount={entries.length}
                    colorScheme="emerald"
                  />
                </div>
                <div>
                  <input
                    type="text"
                    placeholder="Search..."
                    value={col1Search}
                    onChange={(e) => setCol1Search(e.target.value)}
                    className="w-full text-xs bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg px-2.5 py-1.5 text-gray-800 dark:text-gray-200 placeholder-gray-400 focus:ring-2 focus:ring-emerald-500 focus:outline-none shadow-sm"
                  />
                </div>
              </div>
            </div>

            {/* Column 1 Scrollable List */}
            <div className="flex-1 overflow-y-auto p-4 space-y-2.5 divide-y divide-gray-100 dark:divide-gray-800/60">
              {loading ? (
                <div className="py-12 text-center text-xs text-gray-400 animate-pulse">
                  Loading tasks...
                </div>
              ) : col1Entries.length === 0 ? (
                <div className="py-12 text-center text-xs text-gray-400">
                  No tasks found matching selected team filter.
                </div>
              ) : (
                col1Entries.map((entry, index) => {
                  const isPinned = pinnedIds.includes(entry.id);
                  const activeTabs = (entry as any).activeTabs || [];

                  return (
                    <div
                      key={entry.id || index}
                      className="pt-2.5 first:pt-0 rounded-xl transition-all hover:bg-gray-50/80 dark:hover:bg-gray-800/40 p-2"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <button
                            onClick={(e) => togglePin(entry.id, e)}
                            title={isPinned ? 'Unpin sheet' : 'Pin to top of list'}
                            className={`p-1 rounded-lg text-sm transition-transform hover:scale-110 flex-shrink-0 hover:bg-gray-100 dark:hover:bg-gray-800 ${
                              isPinned
                                ? 'text-amber-500 dark:text-amber-400'
                                : 'text-gray-300 dark:text-gray-600 hover:text-amber-500 dark:hover:text-amber-400'
                            }`}
                          >
                            <svg className="w-4 h-4 transform -rotate-45" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M16 12V4h1V2H7v2h1v8l-2 2v2h5.2v6h1.6v-6H18v-2l-2-2z" />
                            </svg>
                          </button>

                          <div className="flex-1 min-w-0">
                            <a
                              href={entry.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs font-bold text-gray-900 dark:text-white hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors truncate block"
                              title="Open Google Sheet"
                            >
                              {getFormattedEntryTitle(entry)}
                            </a>

                            {/* Active Tab Direct Links */}
                            {activeTabs.length > 0 && (
                              <div className="flex items-center gap-1.5 flex-wrap mt-1.5">
                                {activeTabs.slice(0, 4).map((tab: { name: string; url: string }) => (
                                  <a
                                    key={tab.name}
                                    href={tab.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-[10px] bg-white dark:bg-gray-800 hover:bg-emerald-50 dark:hover:bg-emerald-950 text-emerald-600 dark:text-emerald-400 px-2 py-0.5 rounded border border-gray-200 dark:border-gray-700 hover:border-emerald-400 transition-colors truncate max-w-[150px]"
                                  >
                                    ↳ {tab.name}
                                  </a>
                                ))}
                                {activeTabs.length > 4 && (
                                  <span className="text-[10px] text-gray-400">
                                    +{activeTabs.length - 4} more
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* ================================================================= */}
          {/* COLUMN 2: Printable Documents */}
          {/* ================================================================= */}
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-md border border-gray-200 dark:border-gray-800 flex flex-col h-[calc(100vh-140px)] min-h-[700px] overflow-hidden">
            {/* Column Header */}
            <div className="p-4 bg-gradient-to-r from-blue-50/80 to-indigo-50/80 dark:from-blue-950/30 dark:to-indigo-950/30 border-b border-gray-200 dark:border-gray-800 flex-shrink-0">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-blue-600 text-white flex items-center justify-center font-bold shadow-sm">
                    🖨️
                  </div>
                  <div>
                    <h2 className="text-base font-bold text-gray-900 dark:text-white leading-tight">
                      Printable Documents
                    </h2>
                  </div>
                </div>
                <span className="bg-blue-100 dark:bg-blue-900/60 text-blue-700 dark:text-blue-300 text-xs font-bold px-2.5 py-0.5 rounded-full">
                  {col2Docs.length} Docs
                </span>
              </div>

              {/* Search */}
              <div className="mt-3">
                <input
                  type="text"
                  placeholder="Search..."
                  value={col2Search}
                  onChange={(e) => setCol2Search(e.target.value)}
                  className="w-full text-xs bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg px-2.5 py-1.5 text-gray-800 dark:text-gray-200 placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:outline-none shadow-sm"
                />
              </div>
            </div>

            {/* Column 2 Scrollable Documents List */}
            <div className="flex-1 overflow-y-auto p-4 space-y-2.5">
              {col2Docs.length === 0 ? (
                <div className="py-12 text-center text-xs text-gray-400">
                  No printable forms found matching your search.
                </div>
              ) : (
                col2Docs.map((doc) => {
                  const isPinned = pinnedIds.includes(doc.id);

                  return (
                    <div
                      key={doc.id}
                      className="p-2.5 bg-white dark:bg-gray-800/80 rounded-xl border border-gray-200 dark:border-gray-700/80 hover:border-blue-400/80 dark:hover:border-blue-500/60 shadow-sm transition-all group"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          <button
                            onClick={(e) => togglePin(doc.id, e)}
                            title={isPinned ? 'Unpin document' : 'Pin to top of list'}
                            className={`p-1 rounded-lg text-sm transition-transform hover:scale-110 flex-shrink-0 hover:bg-gray-100 dark:hover:bg-gray-800 ${
                              isPinned
                                ? 'text-amber-500 dark:text-amber-400'
                                : 'text-gray-300 dark:text-gray-600 hover:text-amber-500 dark:hover:text-amber-400'
                            }`}
                          >
                            <svg className="w-4 h-4 transform -rotate-45" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M16 12V4h1V2H7v2h1v8l-2 2v2h5.2v6h1.6v-6H18v-2l-2-2z" />
                            </svg>
                          </button>

                          <a
                            href={doc.docUrl || doc.pdfUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs font-bold text-gray-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-400 leading-tight truncate transition-colors block"
                            title="Open Document"
                          >
                            {doc.title}
                          </a>
                        </div>

                        {/* Document Action Buttons */}
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          {(doc.pdfUrl || doc.docUrl) && (
                            <button
                              onClick={() => setPreviewDoc(doc)}
                              className="p-1 sm:p-1.5 rounded-lg text-sm bg-blue-50 hover:bg-blue-600 text-blue-700 hover:text-white dark:bg-blue-950/40 dark:hover:bg-blue-600 dark:text-blue-300 dark:hover:text-white transition-all shadow-sm flex items-center justify-center cursor-pointer hover:scale-110 active:scale-95"
                              title="Preview Document"
                            >
                              <span className="text-sm sm:text-base leading-none">👁️</span>
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* ================================================================= */}
          {/* COLUMN 3: SSD Team Sheets Directory */}
          {/* ================================================================= */}
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-md border border-gray-200 dark:border-gray-800 flex flex-col h-[calc(100vh-140px)] min-h-[700px] overflow-hidden">
            {/* Column Header */}
            <div className="p-4 bg-gradient-to-r from-purple-50/80 to-indigo-50/80 dark:from-purple-950/30 dark:to-indigo-950/30 border-b border-gray-200 dark:border-gray-800 flex-shrink-0">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-purple-600 text-white flex items-center justify-center font-bold shadow-sm text-sm">
                    📑
                  </div>
                  <div>
                    <h2 className="text-base font-bold text-gray-900 dark:text-white leading-tight">
                      All Sheets
                    </h2>
                  </div>
                </div>
                <span className="bg-purple-100 dark:bg-purple-900/60 text-purple-700 dark:text-purple-300 text-xs font-bold px-2.5 py-0.5 rounded-full">
                  {col3Entries.length} Sheets
                </span>
              </div>

              {/* Space-Saving Multi-Select Team Dropdown & Search Input */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3">
                {/* Team Dropdown */}
                <div>
                  <MultiSelectTeamDropdown
                    selectedTeams={col3SelectedTeams}
                    onChange={setCol3SelectedTeams}
                    teams={sortedTeamsList}
                    userAssignedTeams={userAssignedTeams}
                    totalSheetsCount={entries.length}
                    colorScheme="purple"
                  />
                </div>

                {/* Search Input */}
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search..."
                    value={col3Search}
                    onChange={(e) => setCol3Search(e.target.value)}
                    className="w-full text-xs bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg pl-2.5 pr-7 py-1.5 text-gray-800 dark:text-gray-200 placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:outline-none shadow-sm"
                  />
                  {col3Search && (
                    <button
                      onClick={() => setCol3Search('')}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-xs font-bold"
                    >
                      ×
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Column 3 Scrollable Sheets List */}
            <div className="flex-1 overflow-y-auto p-4 space-y-2.5 divide-y divide-gray-100 dark:divide-gray-800/60">
              {loading ? (
                <div className="py-12 text-center text-xs text-gray-400 animate-pulse">
                  Loading sheets...
                </div>
              ) : col3Entries.length === 0 ? (
                <div className="py-12 text-center text-xs text-gray-400">
                  No sheets found matching selected filter.
                </div>
              ) : (
                col3Entries.map((entry, index) => {
                  const isPinned = pinnedIds.includes(entry.id);
                  const activeTabs = (entry as any).activeTabs || [];

                  return (
                    <div
                      key={entry.id || index}
                      className="pt-2.5 first:pt-0 rounded-xl transition-all hover:bg-gray-50/80 dark:hover:bg-gray-800/40 p-2"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <button
                            onClick={(e) => togglePin(entry.id, e)}
                            title={isPinned ? 'Unpin sheet' : 'Pin to top of list'}
                            className={`p-1 rounded-lg text-sm transition-transform hover:scale-110 flex-shrink-0 hover:bg-gray-100 dark:hover:bg-gray-800 ${
                              isPinned
                                ? 'text-amber-500 dark:text-amber-400'
                                : 'text-gray-300 dark:text-gray-600 hover:text-amber-500 dark:hover:text-amber-400'
                            }`}
                          >
                            <svg className="w-4 h-4 transform -rotate-45" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M16 12V4h1V2H7v2h1v8l-2 2v2h5.2v6h1.6v-6H18v-2l-2-2z" />
                            </svg>
                          </button>

                          <div className="flex-1 min-w-0">
                            <a
                              href={entry.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs font-bold text-gray-900 dark:text-white hover:text-purple-600 dark:hover:text-purple-400 transition-colors truncate block"
                              title="Open Google Sheet"
                            >
                              {getFormattedEntryTitle(entry)}
                            </a>

                            {/* Direct Sub-Tab Clickable Links */}
                            {activeTabs.length > 0 && (
                              <div className="flex items-center gap-1.5 flex-wrap mt-1.5">
                                {activeTabs.slice(0, 4).map((tab: { name: string; url: string }) => (
                                  <a
                                    key={tab.name}
                                    href={tab.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-[10px] bg-white dark:bg-gray-800 hover:bg-purple-50 dark:hover:bg-purple-950 text-purple-600 dark:text-purple-400 px-2 py-0.5 rounded border border-gray-200 dark:border-gray-700 hover:border-purple-400 transition-colors truncate max-w-[150px]"
                                  >
                                    ↳ {tab.name}
                                  </a>
                                ))}
                                {activeTabs.length > 4 && (
                                  <span
                                    className="text-[10px] text-gray-400 cursor-help"
                                    title={activeTabs.slice(4).map((t: { name: string }) => t.name).join(', ')}
                                  >
                                    +{activeTabs.length - 4} more
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

        </div>
      </main>

      {/* ===================================================================== */}
      {/* IN-APP DOCUMENT PREVIEW MODAL */}
      {/* ===================================================================== */}
      {previewDoc && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-800 w-full max-w-5xl h-[85vh] flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-150">
            {/* Modal Header */}
            <div className="px-6 py-4 bg-gray-50 dark:bg-gray-800/80 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="text-xs font-mono font-bold px-2 py-1 rounded bg-emerald-100 dark:bg-emerald-950/60 text-emerald-800 dark:text-emerald-300">
                  #{previewDoc.docNumber}
                </span>
                <div>
                  <h3 className="text-sm font-bold text-gray-900 dark:text-white">
                    {previewDoc.title}
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Category: {previewDoc.category} • In-App Document Viewer
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <a
                  href={previewDoc.pdfUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-3 py-1.5 rounded-lg transition-colors flex items-center gap-1 shadow-sm"
                >
                  <span>🖨️ Open Full PDF / Print</span>
                </a>
                <button
                  onClick={() => setPreviewDoc(null)}
                  className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 p-1.5 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Embedded Document Viewer */}
            <div className="flex-1 bg-gray-100 dark:bg-gray-950 relative">
              <iframe
                src={getEmbedUrl(previewDoc.pdfUrl || previewDoc.docUrl)}
                className="w-full h-full border-0"
                title={previewDoc.title}
                allow="autoplay"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
